import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { Zap, Shield, Users, FileText, Building2, ArrowRight, Network, Eye, HandshakeIcon, Target, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsInView(true); }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isInView];
};

const FonctionnementDispositif = () => {
  const [heroRef, heroInView] = useInView();
  const [stepsRef, stepsInView] = useInView();
  const [actorsRef, actorsInView] = useInView();

  const steps = [
    { num: '01', title: 'Accueil & Pré-diagnostic', desc: 'COPPAM reçoit votre demande, analyse votre situation et réalise un pré-diagnostic initial.', icon: HandshakeIcon, gradient: 'from-green-500 to-emerald-500' },
    { num: '02', title: 'Instruction technique', desc: 'F.O.G.E.D.O.M procède à l\'instruction sociale, territoriale et technique de votre dossier.', icon: FileText, gradient: 'from-blue-500 to-cyan-500' },
    { num: '03', title: 'Décision du Fonds', desc: 'FOGEDOM-SCIC examine le rapport et prend la décision d\'intervention solidaire.', icon: Eye, gradient: 'from-purple-500 to-pink-500' },
    { num: '04', title: 'Mise en œuvre', desc: 'O\'SCOP coordonne la mise en œuvre de la garantie et assure le suivi administratif.', icon: Building2, gradient: 'from-amber-500 to-orange-500' },
  ];

  const actors = [
    { title: 'Bénéficiaire', desc: 'Particulier, entreprise ou collectivité ayant besoin d\'une garantie solidaire', icon: Users },
    { title: 'COPPAM', desc: 'Point d\'entrée, accueil, médiation et accompagnement', icon: HandshakeIcon },
    { title: 'F.O.G.E.D.O.M', desc: 'Instruction et analyse technique des dossiers', icon: FileText },
    { title: 'FOGEDOM-SCIC', desc: 'Fonds de décision et d\'intervention solidaire', icon: Shield },
    { title: 'O\'SCOP', desc: 'Gouvernance, coordination et gestion administrative', icon: Building2 },
    { title: 'Experts', desc: 'Réseau d\'experts civils et techniques', icon: Award },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-cyan-600/20 to-blue-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-300 border border-cyan-500/30 mb-6 px-4 py-2">
              <Zap className="w-4 h-4 mr-2" /> Processus clair et transparent
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-cyan-100 to-white bg-clip-text text-transparent">Fonctionnement</span>
              <br />
              <span className="text-3xl md:text-4xl lg:text-5xl bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">du Dispositif</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mb-10 leading-relaxed">
              Un processus <span className="text-cyan-400 font-semibold">structuré et transparent</span> pour accompagner 
              chaque demande de garantie solidaire, de l'accueil à la mise en œuvre.
            </p>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section ref={stepsRef} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${stepsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 mb-4 px-4 py-2"><Target className="w-4 h-4 mr-2" /> Les 4 étapes clés</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Un parcours <span className="text-cyan-400">fluide</span></h2>
          </div>

          <div className="relative">
            {/* Connection line */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-500 via-blue-500 to-amber-500 -translate-x-1/2" />
            
            <div className="space-y-12">
              {steps.map((step, index) => (
                <div key={index} className={`relative flex flex-col lg:flex-row items-center gap-8 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} ${stepsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 200}ms` }}>
                  {/* Content */}
                  <div className={`flex-1 ${index % 2 === 1 ? 'lg:text-right' : ''}`}>
                    <div className={`bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl`}>
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.gradient} flex items-center justify-center mb-6 ${index % 2 === 1 ? 'lg:ml-auto' : ''} shadow-lg`}>
                        <step.icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-3">{step.title}</h3>
                      <p className="text-slate-400 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                  
                  {/* Number circle */}
                  <div className="relative z-10 w-20 h-20 rounded-full bg-slate-900 border-4 border-slate-700 flex items-center justify-center">
                    <span className={`text-2xl font-black bg-gradient-to-br ${step.gradient} bg-clip-text text-transparent`}>{step.num}</span>
                  </div>
                  
                  {/* Empty space for alignment */}
                  <div className="flex-1 hidden lg:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Actors Section */}
      <section ref={actorsRef} className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-b from-purple-600/10 to-transparent rounded-full blur-3xl" /></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-16 transition-all duration-1000 ${actorsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30 mb-4 px-4 py-2"><Network className="w-4 h-4 mr-2" /> Les acteurs</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Un écosystème <span className="text-purple-400">multi-acteurs</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {actors.map((actor, index) => (
              <div key={index} className={`group bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 hover:border-purple-500/50 transition-all duration-500 hover:-translate-y-2 text-center ${actorsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-14 h-14 mx-auto rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4 border border-purple-500/30 group-hover:scale-110 transition-transform">
                  <actor.icon className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{actor.title}</h3>
                <p className="text-sm text-slate-400">{actor.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Prêt à bénéficier de nos <span className="text-cyan-200">garanties</span> ?</h2>
          <p className="text-xl text-white/80 mb-10">Notre équipe vous accompagne à chaque étape de votre demande.</p>
          <Button size="lg" className="bg-white text-cyan-600 hover:bg-slate-100 font-bold px-10 py-6 text-lg shadow-2xl transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
            Démarrer ma demande <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FonctionnementDispositif;
